import { useMemo } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@semsis/ui/AppShell';
import { type DropdownMenuItemConfig } from '@semsis/ui/DropdownMenu';
import { ShellBar } from '@semsis/ui/ShellBar';
import { SideNav, SideNavItem } from '@semsis/ui/SideNav';
import { Spinner } from '@semsis/ui/Spinner';
import { faHouse, faBuilding, faUsers, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { useRequireAuth, useUserInitials, useHasRole, NotAuthorized } from '@semsis/auth-frontend';

export function AdminLayout() {
  const { t } = useTranslation();
  const { loading, logout } = useRequireAuth();
  const { pathname } = useLocation();

  const initials = useUserInitials();
  const hasAdminRole = useHasRole('admin');

  const menuItems: DropdownMenuItemConfig[] = useMemo(
    () => [
      { label: t('menu.logout'), onSelect: logout },
    ],
    [t, logout],
  );

  if (loading) {
    return <Spinner page message={t('app.loading')} />;
  }

  if (!hasAdminRole) {
    return <NotAuthorized onLogout={logout} />;
  }

  return (
    <AppShell
      shellBar={
        <ShellBar
          logo={<img src="/logo.svg" alt="Referee" />}
          title={t('app.title')}
          avatar={initials}
          menuItems={menuItems}
          menuButtonLabel={t('menu.userMenu')}
        />
      }
      sideNav={
        <SideNav>
          <SideNavItem asChild icon={faHouse} label={t('menu.home')} active={pathname === '/'}>
            <NavLink to="/" end />
          </SideNavItem>
          <SideNavItem asChild icon={faBuilding} label={t('menu.federations')} active={pathname.startsWith('/federations')}>
            <NavLink to="/federations" />
          </SideNavItem>
          <SideNavItem asChild icon={faUsers} label={t('menu.users')} active={pathname.startsWith('/users')}>
            <NavLink to="/users" />
          </SideNavItem>
          <SideNavItem asChild icon={faLayerGroup} label={t('menu.catalogs')} active={pathname.startsWith('/catalogs')}>
            <NavLink to="/catalogs" />
          </SideNavItem>
        </SideNav>
      }
    >
      <Outlet />
    </AppShell>
  );
}
